/* Phase 15 item 8 -- Password change banner with persistent dismissal.
 *
 * Banner shows only when user has must_change_password AND has not
 * dismissed it (via /me/ui-preferences). Dismissal writes to backend so
 * it survives across devices; a new must_change_password event (e.g. HR
 * force-reset) clears the dismissed_password_banner flag on the server.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { KeyRound, ShieldAlert, X } from "lucide-react";
import { useAuth } from "@/store/auth";
import api from "@/lib/api";

export default function PasswordChangeBanner() {
  const { t } = useTranslation();
  const user = useAuth((s) => s.user);
  const [dismissed, setDismissed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user?.must_change_password) { setLoaded(true); return; }
    let cancel = false;
    api.get("/me/ui-preferences").then(({ data }) => {
      if (cancel) return;
      setDismissed(!!data?.dismissed_password_banner);
      setLoaded(true);
    }).catch(() => setLoaded(true));
    return () => { cancel = true; };
  }, [user?.must_change_password]);

  const handleDismiss = async () => {
    setDismissed(true);
    try { await api.put("/me/ui-preferences", { dismissed_password_banner: true }); }
    catch { /* still hide locally */ }
  };

  if (!loaded || !user?.must_change_password || dismissed) return null;

  return (
    <div className="fixed top-16 left-0 right-0 z-30 px-4 pointer-events-none" data-testid="must-change-pw-banner">
      <div className="max-w-6xl mx-auto pointer-events-auto">
        <div className="glass-elevated border-amber-500/40 bg-amber-500/[0.08] p-3 flex items-center gap-3">
          <ShieldAlert className="h-5 w-5 text-amber-300 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-amber-100">{t("mcp.title")}</div>
            <div className="text-xs text-foreground/70 truncate">{t("mcp.body")}</div>
          </div>
          <Link to="/me/profile?tab=security" className="shrink-0">
            <button className="text-xs px-3 py-1.5 rounded-lg bg-gradient-primary text-white flex items-center gap-1" data-testid="mcp-goto-security">
              <KeyRound className="h-3 w-3" /> {t("mcp.changeNow")}
            </button>
          </Link>
          <button
            onClick={handleDismiss}
            data-testid="mcp-dismiss"
            aria-label="Dismiss"
            className="shrink-0 text-amber-200/70 hover:text-amber-100 transition p-1"
            title="Dismiss (banner will return on next password expiry)"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
